import { marked } from 'marked';
import { getArticleBySlug, articleLanguage } from '../data/articles';
import { catalogLanguage } from './productTranslations';

const PRODUCT_DESCRIPTIONS = {
  'volume-profile-premium': {
    fr: 'volume-profile-premium.md',
    en: 'volume-profile-premium-en.md',
  },
  'volume-profile-lite': {
    fr: 'volume-profile-lite.md',
    en: 'volume-profile-lite-en.md',
  },
  'vwap-premium': {
    fr: 'vwap-premium.md',
    en: 'vwap-premium-en.md',
  },
  'vwap-lite': {
    fr: 'vwap-lite.md',
    en: 'vwap-lite-en.md',
  },
  'trading-sessions': {
    fr: 'trading-sessions.md',
    en: 'trading-sessions-en.md',
  },
  'asian-session-levels': {
    fr: 'asian-session.md',
    en: 'asian-session-en.md',
  },
  'enhanced-session-levels': {
    fr: 'enhanced-session-levels.md',
    en: 'enhanced-session-levels-en.md',
  },
  'risk-reward': {
    fr: 'risk-reward.md',
    en: 'risk-reward-en.md',
  },
  'open-levels': {
    fr: 'open-levels.md',
    en: 'open-levels-en.md',
  },
  'orb': {
    fr: 'orb.md',
    en: 'orb-en.md',
  },
  'bar-timer': {
    fr: 'fun-bar-timer.md',
    en: 'fun-bar-timer-en.md',
  },
  'nypa': {
    fr: 'ny-price-action.md',
    en: 'ny-price-action-en.md',
  },
  'economic-calendar': {
    fr: 'economic-calendar.md',
    en: 'economic-calendar-en.md',
  },
  'account-locker': {
    fr: 'account-locker.md',
    en: 'account-locker-en.md',
  },
  'footprint': {
    fr: 'footprint-pro.md',
    en: 'footprint-pro-en.md',
  },
  'tape-power-meter': {
    fr: 'tape-power-meter.md',
    en: 'tape-power-meter-en.md',
  },
  'fair-value-gap': {
    fr: 'fair-value-gap.md',
    en: 'fair-value-gap-en.md',
  },
  'cumulative-delta': {
    fr: 'cumulative-delta.md',
    en: 'cumulative-delta-en.md',
  },
  'delta-profile': {
    fr: 'delta-profile.md',
    en: 'delta-profile-en.md',
  },
  'big-trades': {
    fr: 'big-trades.md',
    en: 'big-trades-en.md',
  },
  'anchored-vwap': {
    fr: 'anchored-vwap.md',
    en: 'anchored-vwap-en.md',
  },
  manager: {
    fr: 'manager.md',
    en: 'manager-en.md',
  },
};

const SLUG_MAPPING = {
  'volume-profile': 'volume-profile-premium',
  'vwap-avance': 'vwap-premium',
  'ny-price-action': 'nypa',
  'fun-bar-timer': 'bar-timer',
  'footprint-pro': 'footprint',
  'cumulative-delta-pro': 'cumulative-delta',
  'economic-news': 'economic-calendar',
  'asian-session': 'asian-session-levels',
  'alturion-manager': 'manager',
};

export const loadProductDescription = async (productSlug, language = 'fr') => {
  const mappedSlug = SLUG_MAPPING[productSlug] || productSlug;
  const productFiles = PRODUCT_DESCRIPTIONS[mappedSlug];

  if (!productFiles) {
    console.warn(
      `Aucune description détaillée trouvée pour le produit: ${productSlug} (${mappedSlug})`
    );
    return null;
  }

  const lang = catalogLanguage(language);
  const fileName = productFiles[lang] || productFiles.fr;

  if (!fileName) {
    console.warn(`Aucun fichier trouvé pour ${productSlug} en ${language}`);
    return null;
  }

  try {
    const response = await fetch(`/descriptions/${fileName}`);
    if (!response.ok) {
      throw new Error(`Erreur lors du chargement: ${response.status}`);
    }

    const markdownContent = await response.text();
    if (/^\s*<!doctype html/i.test(markdownContent) || /^\s*<html/i.test(markdownContent)) {
      console.warn(`Description HTML inattendue pour ${productSlug}: ${fileName}`);
      return null;
    }
    return marked(markdownContent);
  } catch (error) {
    console.error(`Erreur lors du chargement de la description pour ${productSlug}:`, error);
    return null;
  }
};

export function rewriteArticleMarkdown(markdown) {
  return markdown
    .replace(/\*\*Meta description\s*:?\*\*[^\n]*\n+/i, '')
    .replace(
      /\]\((?:\.\.\/)+Alturion(?:%20| )Indicators\/docs\/alturionlabs-fiches-seo\/[^)]+\)/g,
      '](/indicators)'
    )
    .replace(/\]\(([^)]+\.md)\)/g, (match, href) => {
      if (/^https?:/i.test(href)) return match;
      const file = href.split('/').pop() || href;
      const slug = file.replace(/-en\.md$/i, '').replace(/\.md$/i, '');
      return `](/resources/${slug})`;
    });
}

export const loadArticle = async (slug, language = 'fr') => {
  if (!getArticleBySlug(slug)) {
    return null;
  }

  const lang = articleLanguage(language);
  const fileName = lang === 'en' ? `${slug}-en.md` : `${slug}.md`;

  try {
    const response = await fetch(`/articles/${fileName}`);
    if (!response.ok) {
      throw new Error(`Erreur lors du chargement: ${response.status}`);
    }

    const markdownContent = await response.text();
    return marked(rewriteArticleMarkdown(markdownContent));
  } catch (error) {
    console.error(`Erreur lors du chargement de l’article ${slug}:`, error);
    return null;
  }
};

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false,
});
